import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, MapPin, Navigation, X, SlidersHorizontal, LayoutGrid, Map as MapIcon } from "lucide-react";
import { api } from "../lib/api";
import { TruckCard } from "../components/TruckCard";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { MapView } from "../components/MapView";
import { TruckGridSkeleton } from "../components/TruckCardSkeleton";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";
import { toast } from "sonner";

export default function SearchResults() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const [ville, setVille] = useState(sp.get("city") || "");
  const [cuisine, setCuisine] = useState(sp.get("cuisine") || "");
  const [openNow, setOpenNow] = useState(sp.get("open_now") === "true");
  const [sort, setSort] = useState(sp.get("sort") || "popularity");
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");

  const load = useCallback(async () => {
    setLoading(true);
    const params = { sort };
    if (sp.get("city")) params.city = sp.get("city");
    if (sp.get("cuisine")) params.cuisine = sp.get("cuisine");
    if (sp.get("open_now") === "true") params.open_now = true;
    if (sp.get("lat")) { params.lat = sp.get("lat"); params.lng = sp.get("lng"); }
    const { data } = await api.get("/trucks", { params });
    setResults(data.results); setCount(data.count); setLoading(false);
  }, [sp, sort]);

  useEffect(() => { load(); }, [load]);

  const apply = () => {
    const p = new URLSearchParams();
    if (ville) p.set("city", ville);
    if (cuisine) p.set("cuisine", cuisine);
    if (openNow) p.set("open_now", "true");
    if (sort) p.set("sort", sort);
    setSp(p);
  };

  const reset = () => { setVille(""); setCuisine(""); setOpenNow(false); setSort("popularity"); setSp(new URLSearchParams()); };

  const geo = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation non disponible");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setSort("proximity"); setSp(new URLSearchParams({ lat: pos.coords.latitude, lng: pos.coords.longitude, sort: "proximity" })); },
      () => toast.error("Position indisponible")
    );
  };

  const activeChips = [];
  if (sp.get("city")) activeChips.push(`Ville : ${sp.get("city")}`);
  if (sp.get("cuisine")) activeChips.push(`Cuisine : ${sp.get("cuisine")}`);
  if (sp.get("open_now") === "true") activeChips.push("Ouvert maintenant");
  if (sp.get("lat")) activeChips.push("Autour de moi");

  const seoCity = sp.get("city");
  const seoCuisine = sp.get("cuisine");
  const seoTitle = seoCity
    ? `Foodtrucks${seoCuisine ? " " + seoCuisine : ""} à ${seoCity}`
    : "Rechercher un foodtruck près de chez vous";
  const seoDesc = seoCity
    ? `Trouvez les foodtrucks${seoCuisine ? " " + seoCuisine : ""} ouverts à ${seoCity} : emplacements du jour, horaires et avis sur StreatEat.`
    : undefined;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <Seo title={seoTitle} description={seoDesc} path="/recherche" />
      <h1 className="text-3xl sm:text-4xl font-display font-extrabold text-urban-teal">Résultats de recherche</h1>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-warm p-4 sm:p-5 mt-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <AddressAutocomplete value={ville} onChange={setVille} onSelect={(s) => setVille(s.city || s.label)} placeholder="Ville" testid="search-ville-input" Icon={MapPin} cityOnly />
          <div className="flex items-center gap-2 border-2 border-cream-bg rounded-xl px-3 py-2.5">
            <Search size={17} className="text-street-gold" />
            <input value={cuisine} onChange={(e) => setCuisine(e.target.value)} placeholder="Cuisine" className="w-full outline-none bg-transparent" data-testid="search-cuisine-input" />
          </div>
          <button onClick={() => setOpenNow(!openNow)} className={`rounded-xl px-3 py-2.5 font-semibold text-sm border-2 transition-colors active:scale-95 ${openNow ? "bg-fresh-mint text-white border-fresh-mint" : "border-cream-bg text-urban-teal hover:border-fresh-mint/50"}`} data-testid="search-open-toggle">Ouvert maintenant</button>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-xl px-3 py-2.5 border-2 border-cream-bg bg-white text-urban-teal font-semibold text-sm outline-none" data-testid="search-sort">
            <option value="popularity">Tri : Popularité</option>
            <option value="proximity">Tri : Proximité</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={apply} className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold gap-1" data-testid="search-apply-btn"><SlidersHorizontal size={15} /> Appliquer</Button>
          <Button onClick={geo} variant="outline" className="rounded-full border-urban-teal/20 text-urban-teal font-semibold gap-1" data-testid="search-geo-btn"><Navigation size={15} /> Autour de moi</Button>
          <Button onClick={reset} variant="ghost" className="rounded-full text-coral-twist font-semibold gap-1" data-testid="search-reset-btn"><X size={15} /> Réinitialiser</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-5">
        <span className="font-display font-bold text-urban-teal" data-testid="results-count">{count} foodtruck{count > 1 ? "s" : ""} trouvé{count > 1 ? "s" : ""}</span>
        {activeChips.map((c) => <span key={c} className="text-xs bg-cream-bg px-3 py-1 rounded-full font-semibold text-urban-teal/70">{c}</span>)}
        <div className="ml-auto flex bg-cream-bg rounded-full p-1">
          <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 transition-all active:scale-95 ${view === "list" ? "bg-white text-urban-teal shadow-warm" : "text-urban-teal/50"}`} data-testid="view-list"><LayoutGrid size={15} /> Liste</button>
          <button onClick={() => setView("map")} className={`px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 transition-all active:scale-95 ${view === "map" ? "bg-white text-urban-teal shadow-warm" : "text-urban-teal/50"}`} data-testid="view-map"><MapIcon size={15} /> Carte</button>
        </div>
      </div>

      {loading ? (
        <TruckGridSkeleton />
      ) : results.length === 0 ? (
        <div className="text-center py-16" data-testid="results-empty">
          <p className="font-display font-bold text-xl text-urban-teal">Aucun foodtruck ne correspond</p>
          <p className="text-urban-teal/60 mt-2">Élargissez votre zone ou votre type de cuisine.</p>
          <Button onClick={reset} className="rounded-full bg-street-gold text-white font-bold mt-5">Réinitialiser la recherche</Button>
        </div>
      ) : view === "map" ? (
        <div className="mt-6"><MapView trucks={results} height={520} /></div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 mt-6">
          {results.map((t, i) => <TruckCard key={t.slug} truck={t} index={i} />)}
        </div>
      )}
    </div>
  );
}
